import { ChevronLeft, ChevronRight, LoaderCircle, LogOut, Menu, Search, X } from "lucide-react";
import {
  type AnchorHTMLAttributes,
  type ButtonHTMLAttributes,
  type ComponentType,
  type ComponentPropsWithoutRef,
  type InputHTMLAttributes,
  type LabelHTMLAttributes,
  type LiHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TdHTMLAttributes,
  type TextareaHTMLAttributes,
  type ThHTMLAttributes,
  type HTMLAttributes,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

function joindreClasses(...classes: Array<string | false | null | undefined>): string | undefined {
  const resultat = classes.filter(Boolean).join(" ");
  return resultat.length > 0 ? resultat : undefined;
}

export type VarianteBouton = "primaire" | "secondaire" | "danger" | "fantome";
export type TailleControle = "compacte" | "standard" | "large";

export interface BoutonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBouton;
  taille?: TailleControle;
  enChargement?: boolean;
}

export const Bouton = forwardRef<HTMLButtonElement, BoutonProps>(function Bouton(
  {
    children,
    className,
    disabled,
    enChargement = false,
    taille = "standard",
    type = "button",
    variante = "primaire",
    ...props
  },
  ref
) {
  return (
    <button
      ref={ref}
      className={joindreClasses("alpha-ui alpha-bouton", className)}
      data-taille={taille}
      data-variante={variante}
      disabled={disabled || enChargement}
      type={type}
      {...props}
    >
      {enChargement ? (
        <LoaderCircle aria-hidden="true" className="alpha-chargement" size={16} />
      ) : null}
      {children}
    </button>
  );
});

export interface BoutonIconeProps extends Omit<BoutonProps, "children"> {
  etiquette: string;
  icone: ReactNode;
}

export const BoutonIcone = forwardRef<HTMLButtonElement, BoutonIconeProps>(function BoutonIcone(
  { className, etiquette, icone, ...props },
  ref
) {
  return (
    <Bouton
      ref={ref}
      aria-label={etiquette}
      className={joindreClasses("alpha-bouton-icone", className)}
      {...props}
    >
      {icone}
    </Bouton>
  );
});

export function GroupeActions({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joindreClasses("alpha-ui alpha-groupe-actions", className)} {...props} />;
}

export interface EtiquetteChampProps extends LabelHTMLAttributes<HTMLLabelElement> {
  htmlFor: string;
}

export function EtiquetteChamp({ className, htmlFor, ...props }: EtiquetteChampProps) {
  return (
    <label
      className={joindreClasses("alpha-ui alpha-etiquette", className)}
      htmlFor={htmlFor}
      {...props}
    />
  );
}

export const ChampTexte = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function ChampTexte({ className, type = "text", ...props }, ref) {
    return (
      <input
        ref={ref}
        className={joindreClasses("alpha-ui alpha-controle", className)}
        type={type}
        {...props}
      />
    );
  }
);

export const ChampNombre = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function ChampNombre({ className, inputMode = "numeric", type = "number", ...props }, ref) {
    return (
      <input
        ref={ref}
        className={joindreClasses("alpha-ui alpha-controle", className)}
        inputMode={inputMode}
        type={type}
        {...props}
      />
    );
  }
);

export const ZoneTexte = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function ZoneTexte({ className, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      className={joindreClasses("alpha-ui alpha-zone-texte", className)}
      {...props}
    />
  );
});

export function CaseACocher({
  className,
  children,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { children?: ReactNode }) {
  return (
    <label className={joindreClasses("alpha-ui alpha-case", className)}>
      <input type="checkbox" {...props} />
      {children}
    </label>
  );
}

export interface OptionRadio {
  valeur: string;
  libelle: ReactNode;
  desactive?: boolean;
}

export interface GroupeBoutonsRadioProps {
  nom: string;
  options: OptionRadio[];
  valeur?: string;
  surChangement?: (valeur: string) => void;
}

export function GroupeBoutonsRadio({
  nom,
  options,
  surChangement,
  valeur
}: GroupeBoutonsRadioProps) {
  return (
    <div className="alpha-ui alpha-empilement" role="radiogroup">
      {options.map((option) => (
        <label className="alpha-radio" key={option.valeur}>
          <input
            checked={valeur === option.valeur}
            disabled={option.desactive}
            name={nom}
            onChange={() => surChangement?.(option.valeur)}
            type="radio"
            value={option.valeur}
          />
          {option.libelle}
        </label>
      ))}
    </div>
  );
}

export function Interrupteur({
  className,
  children,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { children?: ReactNode }) {
  return (
    <label className={joindreClasses("alpha-ui alpha-interrupteur", className)}>
      <input role="switch" type="checkbox" {...props} />
      {children}
    </label>
  );
}

export const ListeDeroulante = forwardRef<
  HTMLSelectElement,
  SelectHTMLAttributes<HTMLSelectElement>
>(function ListeDeroulante({ className, ...props }, ref) {
  return (
    <select
      ref={ref}
      className={joindreClasses("alpha-ui alpha-liste-deroulante", className)}
      {...props}
    />
  );
});

export function ListeDeroulanteRecherchable({
  etiquetteRecherche = "Rechercher",
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { etiquetteRecherche?: string }) {
  return (
    <div className="alpha-ui alpha-groupe-champ">
      <span aria-hidden="true">
        <Search size={16} /> {etiquetteRecherche}
      </span>
      <ListeDeroulante {...props}>{children}</ListeDeroulante>
    </div>
  );
}

export function MessageChamp({
  tonalite = "aide",
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement> & { tonalite?: "aide" | "erreur" }) {
  return (
    <p
      className={joindreClasses("alpha-ui alpha-message-champ", className)}
      data-tonalite={tonalite}
      {...props}
    />
  );
}

export function GroupeChamp({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joindreClasses("alpha-ui alpha-groupe-champ", className)} {...props} />;
}

export type TonaliteBadge = "neutre" | "succes" | "danger" | "information" | "avertissement";

export function Badge({
  className,
  tonalite = "neutre",
  ...props
}: HTMLAttributes<HTMLSpanElement> & { tonalite?: TonaliteBadge }) {
  return (
    <span
      className={joindreClasses("alpha-ui alpha-badge", className)}
      data-tonalite={tonalite}
      {...props}
    />
  );
}

export function Carte({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joindreClasses("alpha-ui alpha-carte", className)} {...props} />;
}

export function Panneau({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return <section className={joindreClasses("alpha-ui alpha-panneau", className)} {...props} />;
}

export function Separateur(props: HTMLAttributes<HTMLHRElement>) {
  return <hr className={joindreClasses("alpha-ui alpha-separateur", props.className)} {...props} />;
}

export function Avatar({
  className,
  image,
  nom,
  ...props
}: HTMLAttributes<HTMLSpanElement> & { image?: string; nom: string }) {
  const initiales = nom
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((partie) => partie[0]?.toUpperCase())
    .join("");
  return (
    <span className={joindreClasses("alpha-ui alpha-avatar", className)} title={nom} {...props}>
      {image ? <img alt="" src={image} /> : initiales}
    </span>
  );
}

export function ListeDescriptive({
  elements
}: {
  elements: Array<{ terme: ReactNode; description: ReactNode }>;
}) {
  return (
    <dl className="alpha-ui alpha-empilement">
      {elements.map((element, index) => (
        <div key={index}>
          <dt>
            <strong>{element.terme}</strong>
          </dt>
          <dd>{element.description}</dd>
        </div>
      ))}
    </dl>
  );
}

export function TexteTronque({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return <span className={joindreClasses("alpha-ui alpha-texte-tronque", className)} {...props} />;
}

export function IndicateurChargement({ etiquette = "Chargement" }: { etiquette?: string }) {
  return <LoaderCircle aria-label={etiquette} className="alpha-ui alpha-chargement" size={20} />;
}

export function Squelette({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      aria-hidden="true"
      className={joindreClasses("alpha-ui alpha-squelette", className)}
      {...props}
    />
  );
}

export function EtatVide({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joindreClasses("alpha-ui alpha-etat-vide", className)} {...props} />;
}

export function MessageErreur({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={joindreClasses("alpha-ui alpha-message-erreur", className)}
      role="alert"
      {...props}
    />
  );
}

export function Alerte({
  className,
  tonalite = "information",
  ...props
}: HTMLAttributes<HTMLDivElement> & { tonalite?: "information" | "avertissement" | "danger" }) {
  return (
    <div
      className={joindreClasses("alpha-ui alpha-alerte", className)}
      data-tonalite={tonalite}
      role="status"
      {...props}
    />
  );
}

export function Notification({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={joindreClasses("alpha-ui alpha-notification", className)}
      role="status"
      {...props}
    />
  );
}

export interface BoiteDialogueProps extends ComponentPropsWithoutRef<"dialog"> {
  ouverte?: boolean;
}

export const BoiteDialogue = forwardRef<HTMLDialogElement, BoiteDialogueProps>(
  function BoiteDialogue({ className, ouverte = false, ...props }, ref) {
    const referenceInterne = useRef<HTMLDialogElement | null>(null);

    useEffect(() => {
      const element = referenceInterne.current;
      if (!element) return;
      if (ouverte && !element.open) element.showModal();
      if (!ouverte && element.open) element.close();
    }, [ouverte]);

    return (
      <dialog
        ref={(element) => {
          referenceInterne.current = element;
          if (typeof ref === "function") ref(element);
          else if (ref) ref.current = element;
        }}
        className={joindreClasses("alpha-ui alpha-dialogue", className)}
        {...props}
      />
    );
  }
);

export function BoiteDialogueConfirmation({
  libelleAnnuler = "Annuler",
  libelleConfirmer = "Confirmer",
  surAnnulation,
  surConfirmation,
  children,
  ...props
}: BoiteDialogueProps & {
  libelleAnnuler?: string;
  libelleConfirmer?: string;
  surAnnulation?: () => void;
  surConfirmation?: () => void;
}) {
  return (
    <BoiteDialogue {...props}>
      <div className="alpha-empilement">
        {children}
        <GroupeActions>
          <Bouton onClick={surAnnulation} variante="secondaire">
            {libelleAnnuler}
          </Bouton>
          <Bouton onClick={surConfirmation} variante="danger">
            {libelleConfirmer}
          </Bouton>
        </GroupeActions>
      </div>
    </BoiteDialogue>
  );
}

export function Tiroir({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return <aside className={joindreClasses("alpha-ui alpha-tiroir", className)} {...props} />;
}

export function InfoBulle({ className, ...props }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={joindreClasses("alpha-ui alpha-info-bulle", className)}
      role="tooltip"
      {...props}
    />
  );
}

export function Popover({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joindreClasses("alpha-ui alpha-popover", className)} {...props} />;
}

export function MenuDeroulant({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={joindreClasses("alpha-ui alpha-menu", className)} role="menu" {...props} />
  );
}

export interface Onglet {
  id: string;
  libelle: ReactNode;
  contenu: ReactNode;
}

export function Onglets({
  onglets,
  ongletActif,
  surChangement
}: {
  onglets: Onglet[];
  ongletActif: string;
  surChangement: (id: string) => void;
}) {
  const actif = onglets.find((onglet) => onglet.id === ongletActif) ?? onglets[0];
  return (
    <div className="alpha-ui alpha-onglets">
      <div role="tablist">
        {onglets.map((onglet) => (
          <button
            aria-selected={onglet.id === actif?.id}
            id={`onglet-${onglet.id}`}
            key={onglet.id}
            onClick={() => surChangement(onglet.id)}
            role="tab"
            type="button"
          >
            {onglet.libelle}
          </button>
        ))}
      </div>
      <div aria-labelledby={actif ? `onglet-${actif.id}` : undefined} role="tabpanel">
        {actif?.contenu}
      </div>
    </div>
  );
}

export function Accordeon({ titre, children }: { titre: ReactNode; children: ReactNode }) {
  return (
    <details className="alpha-ui alpha-panneau">
      <summary>{titre}</summary>
      {children}
    </details>
  );
}

export function FilAriane({
  elements
}: {
  elements: Array<{ href?: string; libelle: ReactNode }>;
}) {
  return (
    <nav aria-label="Fil d'Ariane" className="alpha-ui alpha-fil-ariane">
      <ol>
        {elements.map((element, index) => (
          <li key={index}>
            {element.href ? (
              <a href={element.href}>{element.libelle}</a>
            ) : (
              <span>{element.libelle}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

export function Pagination({
  page,
  totalPages,
  surPagePrecedente,
  surPageSuivante
}: {
  page: number;
  totalPages: number;
  surPagePrecedente: () => void;
  surPageSuivante: () => void;
}) {
  return (
    <nav aria-label="Pagination" className="alpha-ui alpha-pagination">
      <Bouton disabled={page <= 1} onClick={surPagePrecedente} variante="secondaire">
        Précédent
      </Bouton>
      <span>
        Page {page} sur {totalPages}
      </span>
      <Bouton disabled={page >= totalPages} onClick={surPageSuivante} variante="secondaire">
        Suivant
      </Bouton>
    </nav>
  );
}

export interface LienNavigationProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  children: ReactNode;
}

export function LienNavigation({ children, className, ...props }: LienNavigationProps) {
  return (
    <a className={joindreClasses("alpha-ui alpha-lien-navigation", className)} {...props}>
      {children}
    </a>
  );
}

export interface IconeBarreLateraleModerneProps {
  className?: string;
  size?: number;
  "aria-hidden"?: boolean;
}

export interface ElementBarreLateraleModerne {
  id: string;
  libelle: string;
  icone: ComponentType<IconeBarreLateraleModerneProps>;
  description?: string;
  badge?: string;
  desactive?: boolean;
}

export interface UtilisateurBarreLateraleModerne {
  nom: string;
  role?: string;
  initiales?: string;
  statut?: "en-ligne" | "absent" | "hors-ligne";
}

export interface BarreLateraleModerneProps extends HTMLAttributes<HTMLDivElement> {
  elements: ElementBarreLateraleModerne[];
  selection?: string;
  surSelection?: (id: string) => void;
  nomProduit?: string;
  sousTitreProduit?: string;
  initialeProduit?: string;
  utilisateur?: UtilisateurBarreLateraleModerne;
  libelleNavigation?: string;
  libelleRecherche?: string;
  libelleDeconnexion?: string;
  recherche?: boolean;
  reductible?: boolean;
  surDeconnexion?: () => void;
}

export function BarreLateraleModerne({
  className,
  elements,
  initialeProduit = "A",
  libelleDeconnexion = "Déconnexion",
  libelleNavigation = "Navigation principale",
  libelleRecherche = "Rechercher dans la navigation",
  nomProduit = "Alpha",
  recherche = true,
  reductible = true,
  selection,
  sousTitreProduit = "Interface métier",
  surDeconnexion,
  surSelection,
  utilisateur,
  ...props
}: BarreLateraleModerneProps) {
  const [ouvertMobile, definirOuvertMobile] = useState(false);
  const [replie, definirReplie] = useState(false);
  const [texteRecherche, definirTexteRecherche] = useState("");
  const replieEffectif = reductible && replie;
  const selectionCourante = selection ?? elements[0]?.id ?? "";
  const initialesUtilisateur =
    utilisateur?.initiales ??
    utilisateur?.nom
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((partie) => partie[0]?.toUpperCase())
      .join("") ??
    "";

  const elementsFiltres = useMemo(() => {
    const requete = texteRecherche.trim().toLocaleLowerCase("fr-FR");
    if (!requete) return elements;
    return elements.filter((element) =>
      [element.libelle, element.description, element.badge]
        .filter(Boolean)
        .some((valeur) => valeur?.toLocaleLowerCase("fr-FR").includes(requete))
    );
  }, [elements, texteRecherche]);

  function selectionner(element: ElementBarreLateraleModerne) {
    if (element.desactive) return;
    surSelection?.(element.id);
    definirOuvertMobile(false);
  }

  return (
    <div
      className={joindreClasses("alpha-ui alpha-barre-laterale-moderne", className)}
      data-replie={replieEffectif ? "true" : "false"}
      {...props}
    >
      <BoutonIcone
        className="alpha-barre-laterale-mobile"
        etiquette={ouvertMobile ? "Fermer la navigation" : "Ouvrir la navigation"}
        icone={ouvertMobile ? <X size={18} /> : <Menu size={18} />}
        onClick={() => definirOuvertMobile((valeur) => !valeur)}
        variante="secondaire"
      />
      {ouvertMobile ? (
        <button
          aria-label="Fermer la navigation"
          className="alpha-barre-laterale-overlay"
          onClick={() => definirOuvertMobile(false)}
          type="button"
        />
      ) : null}
      <aside
        aria-label={libelleNavigation}
        className="alpha-barre-laterale"
        data-ouvert-mobile={ouvertMobile ? "true" : "false"}
      >
        <header className="alpha-barre-laterale-entete">
          <div className="alpha-barre-laterale-marque">
            <span className="alpha-barre-laterale-logo" aria-hidden="true">
              {initialeProduit.slice(0, 1).toUpperCase()}
            </span>
            <span className="alpha-barre-laterale-texte">
              <strong>{nomProduit}</strong>
              <small>{sousTitreProduit}</small>
            </span>
          </div>
          {reductible ? (
            <BoutonIcone
              className="alpha-barre-laterale-reduction"
              etiquette={replieEffectif ? "Déplier la navigation" : "Replier la navigation"}
              icone={replieEffectif ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
              onClick={() => definirReplie((valeur) => !valeur)}
              variante="fantome"
            />
          ) : null}
        </header>

        {recherche && !replieEffectif ? (
          <div className="alpha-barre-laterale-recherche">
            <Search aria-hidden="true" size={16} />
            <input
              aria-label={libelleRecherche}
              onChange={(evenement) => definirTexteRecherche(evenement.currentTarget.value)}
              placeholder="Rechercher"
              type="search"
              value={texteRecherche}
            />
          </div>
        ) : null}

        <nav aria-label={libelleNavigation} className="alpha-barre-laterale-nav">
          <ul>
            {elementsFiltres.length ? (
              elementsFiltres.map((element) => {
                const Icone = element.icone;
                const actif = element.id === selectionCourante;
                return (
                  <li key={element.id}>
                    <button
                      aria-current={actif ? "page" : undefined}
                      className="alpha-barre-laterale-item"
                      data-actif={actif ? "true" : "false"}
                      disabled={element.desactive}
                      onClick={() => selectionner(element)}
                      title={replieEffectif ? element.libelle : undefined}
                      type="button"
                    >
                      <span className="alpha-barre-laterale-icone">
                        <Icone aria-hidden={true} size={18} />
                      </span>
                      <span className="alpha-barre-laterale-texte">
                        <span>{element.libelle}</span>
                        {element.description ? <small>{element.description}</small> : null}
                      </span>
                      {element.badge ? (
                        <span className="alpha-barre-laterale-badge">{element.badge}</span>
                      ) : null}
                      {replieEffectif ? (
                        <span className="alpha-barre-laterale-info-bulle" role="tooltip">
                          {element.libelle}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })
            ) : (
              <li className="alpha-barre-laterale-vide">Aucun résultat</li>
            )}
          </ul>
        </nav>

        <footer className="alpha-barre-laterale-pied">
          {utilisateur ? (
            <div className="alpha-barre-laterale-utilisateur">
              <span className="alpha-barre-laterale-avatar" aria-hidden="true">
                {initialesUtilisateur}
              </span>
              <span className="alpha-barre-laterale-texte">
                <strong>{utilisateur.nom}</strong>
                {utilisateur.role ? <small>{utilisateur.role}</small> : null}
              </span>
              <span
                aria-label={`Statut ${utilisateur.statut ?? "hors-ligne"}`}
                className="alpha-barre-laterale-statut"
                data-statut={utilisateur.statut ?? "hors-ligne"}
                role="img"
              />
            </div>
          ) : null}
          {surDeconnexion ? (
            <button
              className="alpha-barre-laterale-deconnexion"
              onClick={surDeconnexion}
              title={replieEffectif ? libelleDeconnexion : undefined}
              type="button"
            >
              <LogOut aria-hidden="true" size={18} />
              <span className="alpha-barre-laterale-texte">{libelleDeconnexion}</span>
            </button>
          ) : null}
        </footer>
      </aside>
    </div>
  );
}

export function ConteneurPage({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <main className={joindreClasses("alpha-ui alpha-conteneur-page", className)} {...props} />;
}

export function EnTetePage({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <header className={joindreClasses("alpha-ui alpha-entete-page", className)} {...props} />;
}

export function SectionPage({ className, ...props }: HTMLAttributes<HTMLElement>) {
  return (
    <section className={joindreClasses("alpha-ui alpha-section-page", className)} {...props} />
  );
}

export function GrilleResponsive({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={joindreClasses("alpha-ui alpha-grille-responsive", className)} {...props} />
  );
}

export function Empilement({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joindreClasses("alpha-ui alpha-empilement", className)} {...props} />;
}

export function GroupeHorizontal({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={joindreClasses("alpha-ui alpha-groupe-horizontal", className)} {...props} />
  );
}

export function Tableau({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="alpha-ui alpha-tableau-conteneur">
      <table className={joindreClasses("alpha-tableau", className)} {...props} />
    </div>
  );
}

export function EnteteTableau(props: ThHTMLAttributes<HTMLTableCellElement>) {
  return <th scope="col" {...props} />;
}

export function CelluleTableau(props: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td {...props} />;
}

export function EtatTableauVide({
  colSpan = 1,
  children = "Aucune donnée à afficher"
}: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <tbody>
      <tr>
        <td colSpan={colSpan}>{children}</td>
      </tr>
    </tbody>
  );
}

export function EtatTableauChargement({
  colSpan = 1,
  children = "Chargement des données"
}: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <tbody>
      <tr>
        <td colSpan={colSpan}>
          <IndicateurChargement etiquette={String(children)} />
        </td>
      </tr>
    </tbody>
  );
}

export type { LiHTMLAttributes };
